import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import AdminLayout from './AdminLayout';
import '../App.css';

const AllProfiles = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/profiles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setProfiles(response.data.data || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch profiles' });
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setMessage({ type: 'error', text: 'Error fetching profiles. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = (profile) => {
    // Navigate to profile view page using username
    if (profile.username) {
      navigate(`/profile/${profile.username}`);
    } else if (profile.user_id) {
      // If username not available, try to fetch it or use a different approach
      // For now, we'll show a message
      setMessage({ type: 'error', text: 'Username not available for this profile' });
    } else {
      setMessage({ type: 'error', text: 'Unable to view profile: missing user information' });
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/api/profiles/${profileId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Profile deleted successfully!' });
        fetchProfiles();
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting profile'
      });
    }
  };

  // Filter profiles based on search term
  const filteredProfiles = profiles.filter(profile => {
    const searchLower = searchTerm.toLowerCase();
    return (
      profile.name?.toLowerCase().includes(searchLower) ||
      profile.party_name?.toLowerCase().includes(searchLower) ||
      profile.email_id?.toLowerCase().includes(searchLower) ||
      profile.mobile_number?.includes(searchTerm)
    );
  });

  return (
    <AdminLayout pageTitle="All Profiles" pageSubtitle="View and manage all created profiles">
      <div className="business-form-card">
        <div className="business-form-header">
          <div className="business-form-icon-wrapper">
            <i className="fi fi-rr-users"></i>
          </div>
          <h2 className="business-form-title">All Profiles</h2>
          <p className="business-form-subtitle">Browse and manage all political profiles in the system</p>
        </div>

        {message.text && (
          <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
            <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            <span>{message.text}</span>
          </div>
        )}

        {/* Search Bar */}
        <div className="business-form-group" style={{ marginBottom: '24px' }}>
          <div className="business-upload-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className="fi fi-rr-search" style={{ fontSize: '1.2rem', color: '#64748b' }}></i>
            <input
              type="text"
              placeholder="Search by name, party, email, or mobile number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="business-form-input"
              style={{ flex: 1, margin: 0 }}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading profiles...</p>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="text-center py-5">
            <i className="fi fi-rr-users" style={{ fontSize: '3rem', color: '#64748b', marginBottom: '16px' }}></i>
            <p className="text-muted" style={{ fontSize: '1.1rem' }}>
              {searchTerm ? 'No profiles found matching your search.' : 'No profiles found. Create your first profile!'}
            </p>
            {!searchTerm && (
              <button
                className="business-btn business-btn-primary"
                onClick={() => navigate('/create-profile')}
                style={{ marginTop: '16px' }}
              >
                <i className="fi fi-rr-user-add"></i>
                Create Profile
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="business-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Party</th>
                  <th>Contact</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.map((profile) => (
                  <tr key={profile.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {profile.profile_picture ? (
                          <img
                            src={profile.profile_picture}
                            alt={profile.name}
                            style={{
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid #e2e8f0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="user-avatar-small"
                          style={{
                            display: profile.profile_picture ? 'none' : 'flex',
                            width: '50px',
                            height: '50px',
                            fontSize: '1.2rem'
                          }}
                        >
                          {profile.name?.charAt(0).toUpperCase() || 'P'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong style={{ color: '#1e293b', fontSize: '0.95rem' }}>{profile.name || 'N/A'}</strong>
                      </div>
                    </td>
                    <td>
                      <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{profile.party_name || 'N/A'}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {profile.email_id && (
                          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            <i className="fi fi-rr-envelope" style={{ marginRight: '4px' }}></i>
                            {profile.email_id}
                          </span>
                        )}
                        {profile.mobile_number && (
                          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            <i className="fi fi-rr-phone-call" style={{ marginRight: '4px' }}></i>
                            {profile.mobile_number}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {profile.created_at
                          ? new Date(profile.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="business-table-actions">
                        <button
                          className="business-action-btn view"
                          onClick={() => handleViewProfile(profile)}
                          title="View Profile"
                        >
                          <i className="fi fi-rr-eye"></i>
                        </button>
                        {user?.role === 'superadmin' || user?.role === 'admin' ? (
                          <button
                            className="business-action-btn delete"
                            onClick={() => handleDeleteProfile(profile.id)}
                            title="Delete Profile"
                          >
                            <i className="fi fi-rr-trash"></i>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredProfiles.length > 0 && (
          <div style={{ marginTop: '24px', padding: '16px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center' }}>
            <p style={{ color: '#64748b', margin: 0 }}>
              Showing <strong>{filteredProfiles.length}</strong> of <strong>{profiles.length}</strong> profiles
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AllProfiles;

